import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { closeUserProfile, closeExternalUser, closeUserProfileModals } from "@Reducers/userProfileModal";

// Lazy so the modals (GroupedUsersDetailsModal pulls in SlickGrid) stay out of
// the eager App chunk — they only load on first open.
const UserProfileModal = lazy(() => import("./index"));
const GroupedUsersDetailsModal = lazy(() => import("./GroupedUsersDetailsModal"));
const ExternalUserProfileModal = lazy(() => import("./ExternalUserProfileModal"));

/**
 * Single app-level mount for the user-profile modals, driven by the
 * userProfileModal slice. Plain-DOM grid cells (userTagDom.ts) dispatch
 * openUserProfile/openGroupedUsersModal instead of hosting the modal per cell.
 */
function ProfileModalSlot() {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.userProfileModal.userId);
  if (!userId) return null;
  // Clears only `userId` — the grouped modal (if it opened this one) stays open.
  return <UserProfileModal userId={userId} onClose={() => dispatch(closeUserProfile())} />;
}

function GroupedUsersModalSlot() {
  const dispatch = useDispatch();
  const groupIds = useSelector((state: RootState) => state.userProfileModal.groupIds);
  const groupHeader = useSelector((state: RootState) => state.userProfileModal.groupHeader);
  if (!groupIds?.length) return null;
  return (
    // `GroupedUsersDetailsModalProps.detailsModalHeader` is `?: string` while the slice holds
    // `string | undefined`; under `exactOptionalPropertyTypes` the prop is omitted rather than
    // passed as an explicit `undefined`. The modal falls back to its own default header either way.
    <GroupedUsersDetailsModal
      ids={groupIds}
      {...(groupHeader !== undefined && { detailsModalHeader: groupHeader })}
      closeModal={() => dispatch(closeUserProfileModals())}
    />
  );
}

function ExternalUserModalSlot() {
  const dispatch = useDispatch();
  const externalUser = useSelector((state: RootState) => state.userProfileModal.externalUser);
  if (!externalUser) return null;
  // Clears only `externalUser` — same reasoning as the profile modal.
  return <ExternalUserProfileModal user={externalUser} onClose={() => dispatch(closeExternalUser())} />;
}

function UserProfileModalHost() {
  return (
    <>
      <Suspense fallback={null}>
        <ProfileModalSlot />
      </Suspense>
      <Suspense fallback={null}>
        <GroupedUsersModalSlot />
      </Suspense>
      <Suspense fallback={null}>
        <ExternalUserModalSlot />
      </Suspense>
    </>
  );
}

export default UserProfileModalHost;
